import { Card, UserAvatar, ViewFieldset } from '@payaca/ambrosia';
import {
  AnyCustomFieldDefinition,
  CustomFieldHelperType,
  FieldsetValue,
} from '@payaca/custom-fields';
import { FC } from 'react';

import CustomFieldReadonly from './CustomFieldReadonly';

export type User = {
  publicId: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email?: string | null;
  colour?: string | null;
  avatarUrl?: string | null;
};

type CustomFieldReadonlyGroupProps = {
  definitions: AnyCustomFieldDefinition[];
  value: FieldsetValue | null;
  users?: User[];
};

const CustomFieldReadonlyGroup: FC<CustomFieldReadonlyGroupProps> = (props) => {
  const { definitions, value, users } = props;

  return (
    <>
      {definitions.map((definition) => (
        <ReadonlyField
          key={definition.identifier}
          definition={definition}
          value={value?.[definition.identifier] ?? null}
          users={users}
        />
      ))}
    </>
  );
};

export default CustomFieldReadonlyGroup;

const ReadonlyField: FC<{
  definition: AnyCustomFieldDefinition;
  value: FieldsetValue[string];
  users?: User[];
}> = (props) => {
  const { definition, value, users } = props;

  if (definition.type === 'fieldset') {
    const fieldsetValue = (value as FieldsetValue) ?? null;

    return (
      <ViewFieldset>
        <ViewFieldset.Field
          label={`${definition.label}:`}
          description={definition.description}
        >
          <Card sizeVariant="sm">
            <Card.Body className="pt-1">
              <ViewFieldset>
                <CustomFieldReadonlyGroup
                  definitions={definition.children}
                  value={fieldsetValue}
                  users={users}
                />
              </ViewFieldset>
            </Card.Body>
          </Card>
        </ViewFieldset.Field>
      </ViewFieldset>
    );
  }

  if (definition.type === 'user' && users) {
    const userValue = value as
      | CustomFieldHelperType['user']['value']
      | null
      | undefined;
    return (
      <CustomFieldReadonly
        type={definition.type}
        definition={definition}
        value={userValue}
        customFormatter={(value) => {
          const user = users.find((u) => u.publicId === value?.userId);

          if (!value?.userId) return '-';
          if (!user) return definition.formatValue(value);

          return (
            <div className="flex items-center gap-2">
              <UserAvatar
                enableUserInfoTooltip={false}
                sizeVariant="xs"
                borderColourVariant="white"
                user={{
                  firstName: user.firstName,
                  lastName: user.lastName,
                  emailAddress: user.email,
                  userColour: user.colour,
                  imgSrc: user.avatarUrl,
                }}
              />
              {user.fullName}
            </div>
          );
        }}
      />
    );
  }

  return (
    <CustomFieldReadonly
      type={definition.type}
      definition={definition}
      value={value as CustomFieldHelperType[typeof definition.type]['value']}
    />
  );
};
